<template>
    <div ref="componentRef" class="min-h-screen bg-white">
        <div v-if="loading" class="flex justify-center items-center min-h-[400px]">
            <Loader size="large" />
        </div>
        <div v-else-if="event" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <button @click="goBack"
                class="mb-8 text-gray-600 hover:text-primary transition-colors font-sans flex items-center gap-2">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
                Back to Events
            </button>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div class="lg:col-span-2">
                    <div class="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
                        <div v-if="event.image"
                            class="w-full overflow-hidden flex items-center justify-center bg-gray-100"
                            :style="getEventImageContainerStyle(event, 800)">
                            <img :src="event.image" :alt="event.title" :style="getEventImageStyle(event)"
                                class="w-full h-full object-cover" />
                        </div>
                        <div class="p-8">
                            <div v-if="event.featured" class="mb-4">
                                <span
                                    class="px-3 py-1 bg-accent text-white rounded text-sm font-marketing font-semibold">
                                    Featured Event
                                </span>
                            </div>
                            <h1 class="text-4xl md:text-5xl font-marketing font-bold text-primary mb-4"
                                data-no-translate>
                                {{ event.title }}
                            </h1>
                            <div class="flex flex-wrap items-center gap-4 mb-6 text-gray-600 font-sans">
                                <div class="flex items-center gap-2">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span>{{ formatEventDate(event.date.fullDate) }}</span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>{{ event.time }}</span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span>{{ event.location }}</span>
                                </div>
                                <div v-if="event.ticketPrice" class="flex items-center gap-2">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span class="font-semibold text-primary">${{ event.ticketPrice.toFixed(2) }}</span>
                                </div>
                                <div v-else class="flex items-center gap-2">
                                    <span class="font-semibold text-accent">Free</span>
                                </div>
                            </div>
                            <div class="prose max-w-none mb-8">
                                <div v-if="event.description"
                                    class="text-lg text-gray-700 font-sans leading-relaxed mb-4"
                                    v-html="event.description">
                                </div>
                                <div v-if="event.details" class="text-gray-700 font-sans leading-relaxed">
                                    <div v-html="event.details"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="lg:col-span-1">
                    <div class="bg-white rounded-lg shadow-sm p-8 sticky top-8">
                        <h2 class="text-2xl font-header font-bold text-primary mb-6">Get Tickets</h2>

                        <div v-if="isSoldOut" class="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-md">
                            <p class="text-red-800 font-marketing font-semibold text-lg">* SOLD OUT</p>
                            <p class="text-red-700 font-body text-sm mt-1">This event has reached capacity.</p>
                        </div>

                        <form @submit.prevent="handleSubmit" class="space-y-6" v-if="!isSoldOut">
                            <div>
                                <label for="ticketQuantity"
                                    class="block text-sm font-semibold text-primary mb-2 font-body">
                                    Number of Tickets
                                </label>
                                <div class="flex items-center gap-4 flex-wrap overflow-hidden">
                                    <div class="flex items-center border border-gray-300 rounded-md flex-shrink-0">
                                        <button type="button" @click="decreaseQuantity" :disabled="ticketQuantity <= 1"
                                            class="w-10 h-10 rounded-md border-0 border-r border-gray-300 flex items-center justify-center hover:bg-creme-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M20 12H4" />
                                            </svg>
                                        </button>
                                        <input id="ticketQuantity" v-model.number="ticketQuantity" type="number" min="1"
                                            :max="availableTickets !== null ? Math.min(event.maxTickets || 10, availableTickets) : (event.maxTickets || 10)"
                                            class="w-20 text-center bg-white text-black border-0 px-3 py-2 font-body focus:outline-none focus:ring-1 focus:ring-black min-w-0" />
                                        <button type="button" @click="increaseQuantity"
                                            :disabled="ticketQuantity >= (availableTickets !== null ? Math.min(event.maxTickets || 10, availableTickets) : (event.maxTickets || 10))"
                                            class="w-10 h-10 rounded-md border-0 border-l border-gray-300 flex items-center justify-center hover:bg-creme-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M12 4v16m8-8H4" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <p v-if="event.maxTickets" class="text-xs text-gray-500 mt-2 font-body">
                                    Maximum {{ event.maxTickets }} tickets per order
                                </p>
                                <p v-if="availableTickets !== null && availableTickets < event.maxTickets"
                                    class="text-xs text-orange-600 mt-1 font-body font-semibold">
                                    {{ availableTickets }} ticket(s) remaining
                                </p>
                            </div>

                            <div v-if="event.ticketPrice" class="pt-4 border-t border-gray-200 overflow-hidden">
                                <div class="flex justify-between items-center mb-2 min-w-0 gap-2">
                                    <span class="text-gray-600 font-body flex-shrink-0">Price per ticket</span>
                                    <span class="text-lg font-marketing font-bold text-primary flex-shrink-0">${{
                                        event.ticketPrice.toFixed(2) }}</span>
                                </div>
                                <div
                                    class="flex justify-between items-center text-lg font-marketing font-bold text-primary min-w-0 gap-2">
                                    <span class="flex-shrink-0">Total</span>
                                    <span class="flex-shrink-0">${{ totalPrice.toFixed(2) }}</span>
                                </div>
                            </div>

                            <div class="space-y-4">
                                <div>
                                    <label class="block text-sm font-semibold text-primary mb-2 font-body">
                                        Ticket Holder Names *
                                    </label>
                                    <div class="space-y-3">
                                        <div v-for="(name, index) in formData.ticketNames" :key="index">
                                            <label :for="`ticket-name-${index}`"
                                                class="block text-xs text-gray-600 mb-1 font-body">
                                                Ticket {{ index + 1 }}
                                                <span v-if="isTicketInCart(index)"
                                                    class="text-green-600 font-semibold ml-1">(In Cart)</span>
                                            </label>
                                            <input :id="`ticket-name-${index}`" v-model="formData.ticketNames[index]"
                                                type="text" required :disabled="isTicketInCart(index)" :class="[
                                                    'w-full text-black border rounded-md px-4 py-3 font-body',
                                                    isTicketInCart(index)
                                                        ? 'bg-gray-100 border-gray-300 cursor-not-allowed text-gray-600'
                                                        : 'bg-white border-gray-300 focus:outline-none focus:ring-1 focus:ring-black focus:border-black'
                                                ]" :placeholder="`Ticket holder ${index + 1} name`" />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label for="phone" class="block text-sm font-semibold text-primary mb-2 font-body">
                                        Phone Number (Optional)
                                    </label>
                                    <input id="phone" v-model="formData.phone" type="tel" @blur="validatePhone"
                                        class="w-full bg-white text-black border rounded-md px-4 py-3 font-body focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                                        :class="phoneError ? 'border-red-500' : 'border-gray-300'"
                                        placeholder="(555) 123-4567" />
                                    <p v-if="phoneError" class="text-xs text-red-600 mt-1 font-body">{{ phoneError }}
                                    </p>
                                </div>
                            </div>

                            <button type="button" @click="addTicketsToCart"
                                :disabled="(eventInCart && !hasNewTickets) || isSoldOut" :class="[
                                    'w-full px-6 py-3 rounded-md transition-colors font-marketing font-semibold text-lg flex items-center justify-center gap-2',
                                    (eventInCart && !hasNewTickets) || isSoldOut
                                        ? 'bg-gray-300 text-gray-900 cursor-not-allowed'
                                        : 'bg-accent text-white hover:bg-green-600'
                                ]">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                {{ !eventInCart ? 'Cart' : (hasNewTickets ? 'Add More to Cart' : 'In Cart') }}
                            </button>
                        </form>

                        <div v-if="submissionSuccess" class="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
                            <p class="text-green-800 font-body text-sm">
                                {{ successMessage }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <h1 class="text-4xl font-header font-bold text-primary mb-4">Event Not Found</h1>
            <p class="text-gray-600 font-sans mb-8">The event you are looking for does not exist.</p>
            <button @click="goBack"
                class="bg-accent text-white px-6 py-2 rounded-md hover:bg-green-600 transition-colors font-marketing font-medium">
                Back to Events
            </button>
        </div>
    </div>

</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import Loader from './Loader.vue'
import { publicService } from '../services/public'
import { cartService } from '../services/cart'
import { useLoadingState } from '../composables/useLoadingState'
import { useEventImageStyle } from '../composables/useEventImageStyle'
import { useDataTranslation } from '../composables/useDataTranslation'
import { validatePhoneNumber } from '../utils/phoneValidation'
import { formatTime, formatDate as formatDateEST, normalizeApiDate } from '../utils/date'

const { getEventImageStyle, getEventImageContainerStyle } = useEventImageStyle()

const componentRef = ref(null)

const props = defineProps({
    eventId: {
        type: String,
        required: true
    }
})

const event = ref(null)
const { loading, setLoading } = useLoadingState(true)
const ticketQuantity = ref(1)
const isSubmitting = ref(false)
const submissionSuccess = ref(false)
const phoneError = ref(null)
const formData = ref({
    ticketNames: [''],
    phone: ''
})

const { translatePage, handleLanguageChange: handleTranslation } = useDataTranslation(componentRef, loading, '.min-h-screen.bg-white', setLoading)

const handleLanguageChange = async () => {
    await loadEvent()
    await handleTranslation()
}

onMounted(() => {
    window.addEventListener('language-changed', handleLanguageChange)
})

onUnmounted(() => {
    window.removeEventListener('language-changed', handleLanguageChange)
})

const totalPrice = computed(() => {
    if (!event.value?.ticketPrice) return 0
    return event.value.ticketPrice * ticketQuantity.value
})

const successMessage = computed(() => {
    return 'Tickets added to cart! Redirecting...'
})

const isSoldOut = computed(() => {
    if (!event.value?.maxTickets) return false
    const current = event.value.currentAttendees || 0
    const max = event.value.maxTickets
    return current >= max
})

const eventInCart = computed(() => {
    if (!event.value) return false
    return cartService.cartItems.value.some(
        item => item.eventId === event.value.id
    )
})

const ticketsInCart = computed(() => {
    if (!event.value) return 0
    const cartItem = cartService.cartItems.value.find(
        item => item.eventId === event.value.id
    )
    return cartItem ? cartItem.quantity : 0
})

const ticketNamesInCart = computed(() => {
    if (!event.value) return []
    const cartItem = cartService.cartItems.value.find(
        item => item.eventId === event.value.id
    )
    return cartItem?.ticketNames || []
})

const hasNewTickets = computed(() => {
    return ticketQuantity.value > ticketsInCart.value
})

const isTicketInCart = (index) => {
    return index < ticketsInCart.value
}

const availableTickets = computed(() => {
    if (!event.value?.maxTickets) return null
    const current = event.value.currentAttendees || 0
    const max = event.value.maxTickets
    return Math.max(0, max - current)
})

watch(ticketQuantity, () => {
    updateTicketNames()
})

watch([ticketsInCart, ticketNamesInCart], () => {
    // Restore ticket names from cart when component loads or cart changes
    if (ticketNamesInCart.value.length > 0) {
        const existingNames = [...ticketNamesInCart.value]

        // Update quantity to match or add to what's in cart
        if (ticketQuantity.value < ticketsInCart.value) {
            ticketQuantity.value = ticketsInCart.value
        }

        // Ensure we have enough slots for both cart items and new tickets
        while (formData.value.ticketNames.length < ticketQuantity.value) {
            formData.value.ticketNames.push('')
        }

        // Restore the names from cart
        for (let i = 0; i < existingNames.length; i++) {
            formData.value.ticketNames[i] = existingNames[i]
        }
    }
}, { immediate: true })

watch(() => props.eventId, () => {
    loadEvent()
}, { immediate: false })

onMounted(async () => {
    await loadEvent()
    updateTicketNames()
    await translatePage()
})

async function loadEvent() {
    setLoading(true)
    try {
        const apiEvent = await publicService.getEvent(props.eventId)
        if (apiEvent) {
            const normalizedDate = normalizeApiDate(apiEvent.date)
            const eventDate = normalizedDate ? new Date(normalizedDate) : null
            if (!eventDate || isNaN(eventDate.getTime())) {
                console.error('Invalid date format:', apiEvent.date)
                return
            }

            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: 'America/New_York',
                day: '2-digit',
                month: 'short',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            })
            const parts = formatter.formatToParts(eventDate)
            const day = parts.find(p => p.type === 'day').value
            const month = parts.find(p => p.type === 'month').value
            const timeStr = formatTime(normalizedDate)

            event.value = {
                ...apiEvent,
                ticketPrice: apiEvent.ticket_price,
                maxTickets: apiEvent.max_attendees,
                currentAttendees: apiEvent.current_attendees || 0,
                date: {
                    day,
                    month,
                    fullDate: normalizedDate
                },
                time: timeStr,
                location: apiEvent.location || 'TBA',
                requiresRSVP: !!apiEvent.ticket_price,
                image_crop_dimensions: apiEvent.image_crop_dimensions || null
            }
        }
    } catch (error) {
        console.error('Failed to load event:', error)
        const { getEventById } = await import('../utils/events')
        const foundEvent = await getEventById(props.eventId)
        if (foundEvent) {
            event.value = foundEvent
        }
    } finally {
        setLoading(false)
    }
}

function formatEventDate(dateStr) {
    if (!dateStr) return 'Date TBA'
    return formatDateEST(dateStr, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) || 'Invalid date'
}

function updateTicketNames() {
    const currentLength = formData.value.ticketNames.length
    const targetLength = ticketQuantity.value

    if (targetLength > currentLength) {
        for (let i = currentLength; i < targetLength; i++) {
            formData.value.ticketNames.push('')
        }
    } else if (targetLength < currentLength) {
        formData.value.ticketNames = formData.value.ticketNames.slice(0, targetLength)
    }

    if (formData.value.ticketNames.length === 0) {
        formData.value.ticketNames = ['']
    }
}

function increaseQuantity() {
    const max = availableTickets.value !== null ? Math.min(event.value?.maxTickets || 10, availableTickets.value) : (event.value?.maxTickets || 10)
    if (ticketQuantity.value < max) {
        ticketQuantity.value++
        updateTicketNames()
    }
}

function decreaseQuantity() {
    if (ticketQuantity.value > 1) {
        ticketQuantity.value--
        updateTicketNames()
    }
}

const validatePhone = () => {
    if (!formData.value.phone || formData.value.phone.trim() === '') {
        phoneError.value = null
        return true
    }
    const validation = validatePhoneNumber(formData.value.phone)
    phoneError.value = validation.error
    return validation.isValid
}

function addTicketsToCart() {
    if (!event.value) return

    // If event is in cart but no new tickets, don't do anything
    if (eventInCart.value && !hasNewTickets.value) return

    if (isSoldOut.value) {
        alert('This event is sold out.')
        return
    }

    const maxAvailable = availableTickets.value !== null ? Math.min(event.value.maxTickets || 10, availableTickets.value) : (event.value.maxTickets || 10)
    if (ticketQuantity.value > maxAvailable) {
        alert(`Only ${maxAvailable} ticket(s) available.`)
        return
    }

    // Validate all ticket names (both existing and new)
    const validNames = formData.value.ticketNames.filter(name => name.trim() !== '')
    if (validNames.length !== ticketQuantity.value) {
        alert('Please enter a name for each ticket')
        return
    }

    if (!validatePhone()) {
        alert(phoneError.value || 'Please enter a valid phone number')
        return
    }

    // If event already in cart, only add the new tickets
    if (eventInCart.value && hasNewTickets.value) {
        const newTicketCount = ticketQuantity.value - ticketsInCart.value
        const newTicketNames = formData.value.ticketNames.slice(ticketsInCart.value)
        cartService.addEventToCart(event.value, newTicketCount, newTicketNames)
    } else {
        // First time adding to cart
        cartService.addEventToCart(event.value, ticketQuantity.value, validNames)
    }
}

async function handleSubmit() {
    isSubmitting.value = true

    try {
        await new Promise(resolve => setTimeout(resolve, 1500))


        submissionSuccess.value = true

        setTimeout(() => {
            submissionSuccess.value = false
            formData.value = { ticketNames: [''], phone: '' }
            ticketQuantity.value = 1
        }, 5000)
    } catch (error) {
        console.error('Error submitting form:', error)
    } finally {
        isSubmitting.value = false
    }
}

function goBack() {
    window.location.href = '/events'
}
</script>

<style scoped>
:deep(a) {
    color: #2563eb;
    text-decoration: underline;
    transition: color 0.2s;
}

:deep(a):hover {
    color: #1e40af;
}

:deep(ul) {
    list-style-type: disc;
    margin-left: 1.5em;
    margin-bottom: 0.5em;
}

:deep(ol) {
    list-style-type: decimal;
    margin-left: 1.5em;
    margin-bottom: 0.5em;
}

:deep(li) {
    margin-bottom: 0.25em;
}
</style>
